import { Link } from "react-router-dom";
import {
  Youtube,
  Twitch,
  Scissors,
  Camera,
  Music2,
  Gamepad2,
  Clock,
  Flame,
  TrendingDown,
  AlertTriangle,
  Check,
  X,
  ArrowRight,
  Star,
  ExternalLink,
  Sparkles,
  Bot,
  UserRoundCheck,
  Play,
} from "lucide-react";
import SEO, { breadcrumbSchema } from "../Components/SEO";
import TheNav from "../Components/navbar";
import forCreatorsPage from "../images/creatorPage.png";
import editorPortfolio from "../images/editorpage.png";
import stepOneImage from "../images/editors.png";
import stepTwoImage from "../images/chat.png";
import stepThreeImage from "../images/delivery.png";
import mari from "../images/mari.jpg"
import anteos from "../images/anteos1.jpeg";
import chrislee from "../images/chrislee.png";
import gunslinger from "../images/gunslinger.jpeg";
import { trackEvent } from "../utils/analytics";
import {
  streamerEditorKeywords,
  supportedTwitchGamerCategories,
} from "../utils/seoContent";
import { getSafeExternalUrl } from "../utils/security";

const MINT = "#10b981";
const PURPLE = "#a855f7";
const BG = "#0a0f0d";
const SURFACE = "#141a17";
const HAIRLINE = "rgba(255,255,255,0.06)";
const completedEditPlaceholders = [
  {
    title: "Twitch VOD to YouTube video",
    youtubeUrl: "https://www.youtube.com/watch?v=UuKwmLJPoYc",
    description:
      "A long stream cut into a focused gaming highlight with intro hook, tighter pacing, and clean creator context.",
  },
  {
    title: "Short Stream to YouTube video",
    youtubeUrl: "https://www.youtube.com/watch?v=wKNJ_zUVe78&t=319s",
    description:
      "A short form edit built from one standout moment, with subtitles, zooms, reaction timing, and vertical framing.",
  },
];

function getYouTubeEmbedUrl(youtubeUrl = "") {
  if (!youtubeUrl) return "";

  try {
    const url = new URL(youtubeUrl);
    const host = url.hostname.replace(/^www\./, "");
    let videoId = "";

    if (host === "youtu.be") {
      videoId = url.pathname.split("/").filter(Boolean)[0] || "";
    } else if (host === "youtube.com" || host.endsWith(".youtube.com")) {
      if (url.pathname.startsWith("/shorts/")) {
        videoId = url.pathname.split("/").filter(Boolean)[1] || "";
      } else if (url.pathname.startsWith("/embed/")) {
        videoId = url.pathname.split("/").filter(Boolean)[1] || "";
      } else {
        videoId = url.searchParams.get("v") || "";
      }
    }

    return videoId
      ? `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}`
      : "";
  } catch {
    return "";
  }
}

function ScreenshotSlot({
  src,
  label,
  aspect = "16/9",
  accent = "mint",
}) {
  const color = accent === "purple" ? PURPLE : MINT;
  return (
    <div
      className="relative w-full overflow-hidden rounded-xl border"
      style={{
        aspectRatio: aspect,
        borderColor: HAIRLINE,
        background: `linear-gradient(135deg, ${SURFACE} 0%, #0d1411 100%)`,
        boxShadow: `0 30px 80px -40px ${color}40`,
      }}
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `radial-gradient(circle at 30% 20%, ${color}30, transparent 50%), radial-gradient(circle at 80% 80%, ${PURPLE}20, transparent 50%)`,
        }}
      />
      {src ? (
        <img
          src={src}
          alt={label || "Screenshot"}
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <span
            className="rounded-full border px-3 py-1 text-xs uppercase tracking-wider text-zinc-400"
            style={{ borderColor: HAIRLINE, backgroundColor: "rgba(0,0,0,0.3)" }}
          >
            {label}
          </span>
        </div>
      )}
    </div>
  );
}

function CTAButton({
  to,
  variant = "primary",
  children,
}) {
  const base =
    "inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-all";
  if (variant === "primary") {
    return (
      <Link
        to={to}
        className={`${base} text-[#0a0f0d] hover:brightness-110 font-extrabold`}
        style={{ backgroundColor: MINT, boxShadow: `0 10px 40px -10px ${MINT}80` }}
      >
        {children}
        <ArrowRight className="h-4 w-4" />
      </Link>
    );
  }
  return (
    <Link
      to={to}
      className={`${base} border text-zinc-200 hover:bg-white/5 font-extrabold`}
      style={{ borderColor: HAIRLINE }}
    >
      {children}
    </Link>
  );
}

function Section({
  children,
  className = "",
}) {
  return (
    <section className={`mx-auto w-full max-w-6xl px-6 py-24 sm:py-32 ${className}`}>
      {children}
    </section>
  );
}

function TrustBullet({ children }) {
  return (
    <li className="flex items-center gap-2 text-sm text-zinc-400">
      <Check className="h-4 w-4 shrink-0" style={{ color: MINT }} />
      <span>{children}</span>
    </li>
  );
}

function Card({
  children,
  className = "",
}) {
  return (
    <div
      className={`rounded-xl border p-6 ${className}`}
      style={{ backgroundColor: SURFACE, borderColor: HAIRLINE }}
    >
      {children}
    </div>
  );
}

function PainCard({ icon: Icon, text }) {
  return (
    <Card className="flex items-start gap-4">
      <div
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
        style={{ backgroundColor: "rgba(16,185,129,0.1)" }}
      >
        <Icon className="h-5 w-5" style={{ color: MINT }} />
      </div>
      <p className="text-base text-zinc-200">{text}</p>
    </Card>
  );
}

function CategoryCard({ icon: Icon, title }) {
  return (
    <Card className="group transition-colors hover:border-white/15">
      <Icon className="h-7 w-7 m-auto" style={{ color: MINT }} />
      <h3 className="mt-4 text-lg font-semibold text-zinc-100 text-center">{title}</h3>
    </Card>
  );
}

function InfoCard({ icon: Icon, title, children }) {
  return (
    <Card className="h-full">
      <Icon className="h-7 w-7" style={{ color: MINT }} />
      <h3 className="mt-5 text-xl font-semibold text-zinc-100">{title}</h3>
      <p className="mt-3 text-sm leading-relaxed text-zinc-400">{children}</p>
    </Card>
  );
}

function PlaceholderEditCard({ item }) {
  const safeYoutubeUrl = getSafeExternalUrl(item.youtubeUrl);
  const hasYoutubeUrl = Boolean(safeYoutubeUrl);
  const embedUrl = getYouTubeEmbedUrl(item.youtubeUrl);

  return (
    <Card>
      {embedUrl ? (
        <div className="relative aspect-video overflow-hidden rounded-lg border" style={{ borderColor: HAIRLINE }}>
          <iframe
            className="absolute inset-0 h-full w-full"
            src={embedUrl}
            title={item.title}
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
      ) : hasYoutubeUrl ? (
        <a
          href={safeYoutubeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex aspect-video items-center justify-center rounded-lg border text-center transition-colors hover:border-white/25"
          style={{
            borderColor: HAIRLINE,
            background:
              "linear-gradient(135deg, rgba(16,185,129,0.12), rgba(168,85,247,0.14))",
          }}
        >
          <span className="inline-flex items-center gap-2 px-4 text-xs font-semibold uppercase tracking-wider text-zinc-300">
            <Play className="h-4 w-4" />
            Watch completed edit
          </span>
        </a>
      ) : (
        <div
          className="flex aspect-video items-center justify-center rounded-lg border text-center"
          style={{
            borderColor: HAIRLINE,
            background:
              "linear-gradient(135deg, rgba(16,185,129,0.12), rgba(168,85,247,0.14))",
          }}
        >
          <span className="px-4 text-xs font-semibold uppercase tracking-wider text-zinc-400">
            Add a YouTube link here
          </span>
        </div>
      )}

      <h3 className="mt-5 text-lg font-semibold text-zinc-100">{item.title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-zinc-400">
        {item.description}
      </p>
      {hasYoutubeUrl && (
        <a
          href={safeYoutubeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-5 inline-flex items-center gap-1.5 text-xs font-medium transition-opacity hover:opacity-80"
          style={{ color: MINT }}
        >
          Open on YouTube
          <ExternalLink className="h-3 w-3" />
        </a>
      )}
    </Card>
  );
}

function Step({
  n,
  title,
  description,
  accent = "mint",
  screenshotLabel,
}) {
  const color = accent === "purple" ? PURPLE : MINT;
  return (
    <div
      className="flex flex-col rounded-xl border p-6 transition-colors hover:border-white/15"
      style={{
        backgroundColor: SURFACE,
        borderColor: `${color}33`,
        boxShadow: `0 20px 60px -40px ${color}50`,
      }}
    >
      <div className="flex items-center gap-3">
        <div
          className="flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold"
          style={{ backgroundColor: color, color: "#0a0f0d" }}
        >
          {n}
        </div>
        <span
          className="text-xs font-medium uppercase tracking-wider"
          style={{ color }}
        >
          Step {n}
        </span>
      </div>
      <h3 className="mt-5 text-lg font-semibold text-zinc-100">{title}</h3>
      <p className="mt-2 text-sm text-zinc-400">{description}</p>
      <div className="mt-5">
        <ScreenshotSlot
          src={
            n === 1
              ? stepOneImage
              : n === 2
                ? stepTwoImage
                : stepThreeImage
          }
          label={screenshotLabel}
          aspect="4/3"
          accent={accent}
        />
      </div>
    </div>
  );
}

function StarRating() {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className="h-4 w-4 fill-current"
          style={{ color: "#fbbf24" }}
        />
      ))}
    </div>
  );
}

function TestimonialCard({ data }) {
  const color = data.accent === "purple" ? PURPLE : MINT;
  const safeCtaUrl = getSafeExternalUrl(data.ctaUrl);
  return (
    <div
      className="flex flex-col rounded-xl border p-6 transition-colors hover:border-white/15"
      style={{
        backgroundColor: SURFACE,
        borderColor: `${color}33`,
        boxShadow: `0 20px 60px -40px ${color}50`,
      }}
    >
      <div className="flex items-center gap-4">
        <img
          className="flex h-12 w-12 items-center justify-center rounded-full text-sm font-bold"
          src={data.imgLink}
          style={{
            //background: `linear-gradient(135deg, ${color}40, ${color}15)`,
            color,
            border: `1px solid ${color}30`,
          }} />
        <div>
          <p className="text-sm font-semibold text-zinc-100">{data.name}</p>
          <p className="text-xs text-zinc-400">{data.role}</p>
        </div>
      </div>
      <div className="mt-4">
        <StarRating />
      </div>
      <p className="mt-4 text-sm leading-relaxed text-zinc-300">
        “{data.review}”
      </p>
      <div className="mt-5 flex flex-wrap gap-2">
        {data.stats.map((stat) => (
          <span
            key={stat}
            className="rounded-full border px-2.5 py-1 text-xs font-medium"
            style={{
              borderColor: `${color}25`,
              backgroundColor: `${color}10`,
              color,
            }}
          >
            {stat}
          </span>
        ))}
      </div>
      {safeCtaUrl && (
        <a
          href={safeCtaUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-5 inline-flex items-center gap-1.5 text-xs font-medium transition-opacity hover:opacity-80"
          style={{ color }}
        >
          {data.ctaLabel}
          <ExternalLink className="h-3 w-3" />
        </a>
      )}
    </div>
  );
}

function CompareColumn({
  title,
  items,
  positive,
  highlight,
}) {
  const Icon = positive ? Check : X;
  return (
    <Card
      className={highlight ? "" : "opacity-80"}
      {...(highlight
        ? {
          style: {
            backgroundColor: SURFACE,
            borderColor: `${MINT}40`,
            boxShadow: `0 0 0 1px ${MINT}20, 0 20px 60px -30px ${MINT}40`,
          },
        }
        : {})}
    >
      <div className="mb-5 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-zinc-100">{title}</h3>
        {highlight && (
          <span
            className="rounded-full px-2.5 py-0.5 text-xs font-semibold"
            style={{ backgroundColor: `${MINT}20`, color: MINT }}
          >
            Kre8
          </span>
        )}
      </div>
      <ul className="space-y-3">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-3 text-sm text-zinc-300">
            <Icon
              className="mt-0.5 h-4 w-4 shrink-0"
              style={{ color: positive ? MINT : "#71717a" }}
            />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

export default function ForCreatorsPage() {
  return (
    <>
      <SEO
        title="Hire Twitch Editors and Prepare Clipping Campaigns | Kre8"
        description="Find Twitch editors for custom videos and get early access to clipping campaigns that turn streams into TikToks, Shorts, and Reels."
        canonicalPath="/hire-video-editor-for-twitch"
        structuredData={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Hire a Twitch Editor", path: "/hire-video-editor-for-twitch" },
        ])}
      />
      <TheNav />
      <main
        className="min-h-screen antialiased"
        style={{ backgroundColor: BG, color: "#e5e7eb" }}
      >
        {/* HERO */}
        <Section className="relative overflow-hidden">
          <div
            aria-hidden
            className="pointer-events-none absolute left-1/4 top-0 -z-0 h-[500px] w-[500px] -translate-y-1/4 rounded-full opacity-25 blur-3xl"
            style={{ background: `radial-gradient(circle, ${MINT}, transparent 60%)` }}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute right-1/4 top-0 -z-0 h-[500px] w-[500px] -translate-y-1/4 rounded-full opacity-25 blur-3xl"
            style={{ background: `radial-gradient(circle, ${PURPLE}, transparent 60%)` }}
          />
          <div className="relative mx-auto max-w-3xl text-center">
            <p
              className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium uppercase tracking-wider"
              style={{ borderColor: HAIRLINE, color: MINT }}
            >
              <Sparkles className="h-3.5 w-3.5" />
              Find Twitch editors and clippers
            </p>
            <h1 className="mt-6 text-5xl font-bold tracking-tight text-white sm:text-6xl md:text-7xl">
              Hire a Video Editor for Twitch
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg text-zinc-400 sm:text-xl">
              Find editors for custom Twitch videos and prepare clipping
              campaigns that turn stream moments into TikToks, Shorts, and
              Reels.
            </p>
            <ul className="mx-auto mt-8 flex max-w-2xl flex-wrap justify-center gap-x-6 gap-y-3">
              <TrustBullet>Editors experienced with Twitch and gaming pacing</TrustBullet>
              <TrustBullet>Early access to clipping campaigns for stream growth</TrustBullet>
              <TrustBullet>10 gaming and creator categories</TrustBullet>
              <TrustBullet>No hidden platform fees</TrustBullet>
            </ul>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <CTAButton to="/gaming-video-editors" onClick={() =>
                trackEvent("find_editor_click", {
                  location: "hero",
                })
              }
              >Find My Editor</CTAButton>
              <CTAButton to="/launch-clipping-campaign" variant="secondary">
                Prepare a Clipping Campaign
              </CTAButton>
              <CTAButton to="/signup?role=creator&focus=editing-and-clipping" variant="secondary" onClick={() =>
                trackEvent("signup_click", {
                  location: "hero",
                })
               } >
                Signup
              </CTAButton>
            </div>
          </div>
          <div className="relative mx-auto mt-16 max-w-5xl">
            <ScreenshotSlot src={forCreatorsPage} aspect="16/9" accent="mint" />
          </div>
        </Section>

        <Section className="!py-16">
          <div className="mx-auto max-w-3xl text-center">
            <p
              className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium uppercase tracking-wider"
              style={{ borderColor: HAIRLINE, color: MINT }}
            >
              Built for Twitch gamers
            </p>
            <h2 className="mt-5 text-4xl font-bold tracking-tight text-white sm:text-5xl">
              Editors for videos, clippers for campaigns
            </h2>
            <p className="mt-5 text-lg text-zinc-400">
              Kre8 helps Twitch streamers turn raw VODs, ranked games, funny
              moments, clutch plays, and long streams into content for YouTube,
              TikTok, Shorts, and Reels.
            </p>
          </div>
          <div className="mx-auto mt-10 flex max-w-5xl flex-wrap justify-center gap-3">
            {supportedTwitchGamerCategories.map((category) => (
              <span
                key={category}
                className="rounded-full border px-4 py-2 text-sm font-semibold text-zinc-200"
                style={{ borderColor: HAIRLINE, backgroundColor: SURFACE }}
              >
                {category}
              </span>
            ))}
          </div>
          <p className="mx-auto mt-8 max-w-2xl text-center text-base text-zinc-400">
            Start with a creator brief for custom edits, or get ready to open a
            clipping campaign when you want multiple clippers focused on short
            form growth.
          </p>
        </Section>

        <Section>
          <div className="mx-auto max-w-3xl text-center">
            <p
              className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium uppercase tracking-wider"
              style={{ borderColor: HAIRLINE, color: PURPLE }}
            >
              Streamer editor guide
            </p>
            <h2 className="mt-5 text-4xl font-bold tracking-tight text-white sm:text-5xl">
              What does a streamer editor actually do?
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-zinc-400">
              A streamer editor turns raw Twitch VODs, clips, funny moments,
              ranked games, and long livestreams into videos people will
              actually watch after the stream ends. A clipper focuses on short
              form moments that can travel on TikTok, Shorts, and Reels.
            </p>
          </div>
          <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            <InfoCard icon={Twitch} title="Twitch VOD editor">
              A Twitch VOD editor watches longer stream footage, finds the best
              moments, removes dead air, keeps context, and shapes the VOD into
              a highlight or YouTube gaming video.
            </InfoCard>
            <InfoCard icon={Scissors} title="Stream clip editor">
              A stream clip editor focuses on short form moments for TikTok,
              Shorts, and Reels, usually adding captions, zooms, pacing, and a
              hook in the first few seconds.
            </InfoCard>
            <InfoCard icon={Flame} title="Clipping campaigns">
              Campaigns let multiple clippers work around your stream moments
              so you can test more ideas and find clips that pull viewers back.
            </InfoCard>
            <InfoCard icon={Gamepad2} title="Gaming video editor">
              A gaming video editor understands the game, the audience, the
              creator’s humor, and the difference between a random clip and a
              moment worth posting.
            </InfoCard>
          </div>
        </Section>

        <Section>
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-4xl font-bold tracking-tight text-white sm:text-5xl">
              Human stream editor vs AI clip tools
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-zinc-400">
              AI clip tools can help find moments quickly, but Twitch gamers
              often need judgment: what matters in the match, why the joke
              lands, when context matters, and how to make a clip feel native
              to the creator’s channel.
            </p>
          </div>
          <div className="mt-14 grid gap-5 md:grid-cols-2">
            <InfoCard icon={Bot} title="Use tools when speed matters">
              Automated clipping can be useful for quick drafts, simple
              highlights, and testing whether a stream moment has potential.
              It is fastest when the edit only needs trimming and basic
              formatting.
            </InfoCard>
            <InfoCard icon={UserRoundCheck} title="Hire an editor when quality matters">
              A human streamer editor can build story, pacing, captions, comedic
              timing, context, and recurring style, especially for Twitch VODs,
              YouTube gaming videos, and clips tied to a specific game.
            </InfoCard>
          </div>
        </Section>

        <Section>
          <div className="mx-auto max-w-3xl text-center">
            <p
              className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium uppercase tracking-wider"
              style={{ borderColor: HAIRLINE, color: MINT }}
            >
              Example edit types
            </p>
            <h2 className="mt-5 text-4xl font-bold tracking-tight text-white sm:text-5xl">
              Some examples from our previous clients
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-zinc-400">
              Here are a few videos that have been edited through our site. We support many games for both Kick AND Twitch streamers from IRL to League of Legends and many more.
            </p>
          </div>
          <div className="mt-14 grid gap-5 md:grid-cols-2">
            {completedEditPlaceholders.map((item) => (
              <PlaceholderEditCard key={item.title} item={item} />
            ))}
          </div>
        </Section>

        <Section>
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-4xl font-bold tracking-tight text-white sm:text-5xl">
              Editing Is Slowing Down Your Channel
            </h2>
          </div>
          <div className="mt-14 grid gap-4 sm:grid-cols-2">
            <PainCard icon={Clock} text="Hours lost editing every upload" />
            <PainCard icon={Flame} text="Burnout from clips, shorts, and long form videos" />
            <PainCard
              icon={TrendingDown}
              text="Inconsistent uploads hurting channel growth"
            />
            <PainCard
              icon={AlertTriangle}
              text="Low-quality freelancers wasting your time"
            />
          </div>
          <div className="mt-12 text-center">
            <p className="mx-auto max-w-2xl text-lg text-zinc-300">
              The faster you upload, the faster you improve. Outsourcing editing gives you more
              time to create.
            </p>
            <div className="mt-8">
              <CTAButton to="/gaming-video-editors" onClick={() =>
                trackEvent("find_editor_click", {
                  location: "2ndlast",
                })
              }>Hire an Editor</CTAButton>
            </div>
          </div>
        </Section>

        <Section>
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-4xl font-bold tracking-tight text-white sm:text-5xl">
              Editors for Twitch Streamers and Gaming Creators
            </h2>
            <p className="mt-5 text-lg text-zinc-400">
              Work with editors for custom videos and use clipping campaigns
              when you want more short form chances from each stream.
            </p>
          </div>
          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <CategoryCard icon={Youtube} title="YouTube Gaming Videos" />
            <CategoryCard icon={Twitch} title="Twitch Highlights" />
            <CategoryCard icon={Scissors} title="Stream Clips" />
            <CategoryCard icon={Flame} title="Clipping Campaigns" />
            <CategoryCard icon={Camera} title="Twitch VODs" />
            <CategoryCard icon={Music2} title="TikToks & Reels" />
            <CategoryCard icon={Gamepad2} title="Gaming Content" />
          </div>
        </Section>

        <Section>
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-4xl font-bold tracking-tight text-white sm:text-5xl">
              How Twitch content work starts
            </h2>
            <p className="mt-5 text-lg text-zinc-400">
              Three simple steps from raw stream footage to content worth posting.
            </p>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <Step
              n={1}
              accent="mint"
              title="Browse editors and portfolios"
              description="Filter by game, stream editing style, portfolio examples, and short form experience before you ever message."
              screenshotLabel="Editor directory"
            />
            <Step
              n={2}
              accent="purple"
              title="Send footage and direction"
              description="Share Twitch VODs, stream clips, timestamps, references, music ideas, and campaign goals in one place."
              screenshotLabel="Project brief view"
            />
            <Step
              n={3}
              accent="mint"
              title="Publish what works"
              description="Review Twitch highlights, stream clips, YouTube uploads, Shorts, TikToks, or Reels and keep building from the strongest ideas."
              screenshotLabel="Review & approval"
            />
          </div>
          <p className="mt-12 text-center text-base text-zinc-400">
            Direct communication. No complicated hiring process. See a portfolio you like, send them a message.
          </p>
        </Section>

        <Section>
          <div className="mx-auto max-w-3xl text-center">
            <p
              className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium uppercase tracking-wider"
              style={{ borderColor: HAIRLINE, color: PURPLE }}
            >
              Search terms Kre8 is built around
            </p>
            <h2 className="mt-5 text-4xl font-bold tracking-tight text-white sm:text-5xl">
              Find the right kind of stream editor
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-zinc-400">
              Whether you call it a streamer editor, Twitch editor, Twitch VOD
              editor, gaming video editor, clipper, or stream clip editor, the
              goal is the same: turn livestream content into videos that help
              your channel stay active between streams.
            </p>
          </div>
          <div className="mx-auto mt-10 flex max-w-4xl flex-wrap justify-center gap-3">
            {streamerEditorKeywords.map((keyword) => (
              <span
                key={keyword}
                className="rounded-full border px-4 py-2 text-sm font-semibold text-zinc-200"
                style={{ borderColor: HAIRLINE, backgroundColor: SURFACE }}
              >
                {keyword}
              </span>
            ))}
          </div>
        </Section>

        <Section>
          <div className="mx-auto max-w-2xl text-center">
            <p
              className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium uppercase tracking-wider"
              style={{ borderColor: HAIRLINE, color: MINT }}
            >
              Creator stories
            </p>
            <h2 className="mt-5 text-4xl font-bold tracking-tight text-white sm:text-5xl">
              Trusted by creators who upload more
            </h2>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <TestimonialCard
              data={{
                name: "Komari",
                role: "League of Legends Streamer",
                review:
                  "Helped me figure out what I need when my Twitch VODs are edited. The process was way easier than hiring on generic freelance sites.",
                stats: ["Weekly uploads", "6hr turnaround"],
                ctaLabel: "View Channel",
                ctaUrl: "https://www.twitch.tv/komari",
                imgLink: mari,
                accent: "mint",
              }}
            />
            <TestimonialCard
              data={{
                name: "Anteos1",
                role: "YouTube Creator",
                review:
                  "I barely had time to get into editing after coming home from school. Massive boost in views after working with a Kre8 editor.",
                stats: ["Music video essays", "In depth edits"],
                ctaLabel: "View Channel",
                ctaUrl: "https://www.youtube.com/@anteos1ttv",
                imgLink: anteos,
                accent: "purple",
              }}
            />
            <TestimonialCard
              data={{
                name: "lolgunslinger",
                role: "League of Legends Streamer",
                review:
                  "Understands TikTok pacing. My average view duration went up 40% in the first month and my views + comments increased.",
                stats: ["+40% retention", "Free thumbnail"],
                ctaLabel: "View Profile",
                ctaUrl: "https://www.twitch.tv/lolgunslinger",
                imgLink: gunslinger,
                accent: "purple",
              }} />
            <TestimonialCard
              data={{
                name: "chrisleesin",
                role: "League of Legends Streamer",
                review:
                  "The turnaround is unreal. I send all the clips, song choice, what to focus on and it's done perfectly. Editing videos in batches was a HUGE help.",
                stats: ["2x output", "24hr delivery", "Free thumbnail"],
                ctaLabel: "View Channel",
                ctaUrl: "https://www.twitch.tv/chrisleesin",
                imgLink: chrislee,
                accent: "mint",
              }}
            />
          </div>
        </Section>

        {/* TRUST / COMPARE */}
        <Section>
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-4xl font-bold tracking-tight text-white sm:text-5xl">
              Built for Creators, Not Generic Freelancing
            </h2>
          </div>
          <div className="mt-14 grid gap-5 md:grid-cols-2">
            <CompareColumn
              title="Kre8"
              positive
              highlight
              items={[
                "Editors focused on Twitch and gaming content",
                "Direct creator and editor communication",
                "Campaign paths for clippers",
                "Long term working relationships",
                "Transparent pricing",
              ]}
            />
            <CompareColumn
              title="Generic freelance sites"
              positive={false}
              items={[
                "Random freelancers",
                "Inconsistent quality",
                "Platform heavy experience",
                "Hard to find Twitch and gaming editors",
              ]}
            />
          </div>
          <div className="mt-12 text-center">
            <CTAButton to="/gaming-video-editors" onClick={() =>
                trackEvent("find_editor_click", {
                  location: "lastEditorButton",
                })
              }>Pick Your Editor</CTAButton>
            <div className="mt-3">
              <CTAButton to="/launch-clipping-campaign" variant="secondary">
                Plan a Campaign
              </CTAButton>
            </div>
          </div>
        </Section>

        {/* SOCIAL PROOF */}
        <Section className="!py-16">
          <div className="mx-auto max-w-2xl text-center">
            <p
              className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium uppercase tracking-wider"
              style={{ borderColor: HAIRLINE, color: PURPLE }}
            >
              Real editors, real work
            </p>
            <h2 className="mt-5 text-3xl font-bold tracking-tight text-white sm:text-4xl">
              See who's editing for creators like you
            </h2>
          </div>
          <div className="relative mx-auto mt-10 max-w-5xl">
            <ScreenshotSlot
              src={editorPortfolio}
              label="Featured editors grid"
              aspect="21/9"
              accent="purple"
            />
          </div>
        </Section>

        {/* FINAL CTA */}
        <Section className="!py-16">
          <div className="mx-auto max-w-3xl text-center">
            <p
              className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium uppercase tracking-wider"
              style={{ borderColor: HAIRLINE, color: MINT }}
            >
              Browse by intent
            </p>
            <h2 className="mt-5 text-4xl font-bold tracking-tight text-white sm:text-5xl">
              Find Twitch editors and clipping campaign paths
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-zinc-400">
              Browse gaming video editors by category, learn what kind of
              editor fits your stream, or get early access to clipping campaigns.
            </p>
          </div>
          <div className="mx-auto mt-10 grid max-w-3xl gap-4 sm:grid-cols-3">
            <CTAButton to="/gaming-video-editors" variant="secondary">
              Editors by Game
            </CTAButton>
            <CTAButton to="/streamer-editor" variant="secondary">
              Streamer Editor Guide
            </CTAButton>
            <CTAButton to="/clipping-campaigns" variant="secondary">
              Clipping Campaigns
            </CTAButton>
          </div>
        </Section>

        <Section className="relative overflow-hidden">
          <div
            aria-hidden
            className="pointer-events-none absolute left-1/3 top-1/2 -z-0 h-[400px] w-[500px] -translate-y-1/2 rounded-full opacity-20 blur-3xl"
            style={{ background: `radial-gradient(circle, ${MINT}, transparent 60%)` }}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute right-1/3 top-1/2 -z-0 h-[400px] w-[500px] -translate-y-1/2 rounded-full opacity-20 blur-3xl"
            style={{ background: `radial-gradient(circle, ${PURPLE}, transparent 60%)` }}
          />
          <div className="relative mx-auto max-w-3xl text-center">
            <h2 className="text-5xl font-bold tracking-tight text-white sm:text-6xl">
              Upload More. Edit Less.
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-lg text-zinc-400">
              Find editors for custom content or get early access to clipping
              campaigns for Twitch growth.
            </p>
            <div className="mt-10 flex justify-center">
              <CTAButton to="/signup?role=creator&focus=editing-and-clipping" onClick={() =>
                trackEvent("signup_click", {
                  location: "bottomSignupClick",
                })
              }>Start Growing</CTAButton>
            </div>
            <p className="mt-6 text-sm text-white font-extrabold">
              Start with Twitch editors, clippers, and creator growth in one place.
            </p>
          </div>
        </Section>
      </main>
    </>
  );
}
